import { normalizeAssessmentLanguage, uiLanguage } from './assessmentLanguage';

const hasValue = (value) => value !== undefined && value !== null && value !== '';

const firstValue = (...values) => values.find(hasValue);

const getLatestSession = (sessions = {}) => {
  const entries = Object.entries(sessions || {})
    .filter(([, session]) => session && typeof session === 'object')
    .sort(([, left], [, right]) => {
      const leftTime = Date.parse(left.createdAt || '') || 0;
      const rightTime = Date.parse(right.createdAt || '') || 0;
      return rightTime - leftTime;
    });

  if (!entries.length) return {};
  const [useruuid, session] = entries[0];
  return { useruuid, ...session };
};

export const resolveSubmissionContext = ({
  userInfo = {},
  urlParams = {},
  activeSession = {},
  sessions = {},
} = {}) => {
  const preferredUserUuid = firstValue(
    urlParams.useruuid,
    userInfo.useruuid,
    activeSession.useruuid,
  );
  const matchingSession = preferredUserUuid && sessions[preferredUserUuid]
    ? { useruuid: preferredUserUuid, ...sessions[preferredUserUuid] }
    : getLatestSession(sessions);
  const profile = {
    ...(matchingSession.userInfo || {}),
    ...(activeSession.userInfo || {}),
    ...userInfo,
  };

  const evaluationUuid = firstValue(urlParams.evaluation_uuid, userInfo.evaluation_uuid,
    activeSession.evaluation_uuid, matchingSession.evaluation_uuid) || '';
  const sources = [urlParams, userInfo, activeSession, matchingSession]
    .filter(source => !source.evaluation_uuid || source.evaluation_uuid === evaluationUuid);
  const binding = sources.find(source => source.question_bank_server_bound && source.question_bank_version)
    || sources.find(source => source.question_bank_version) || {};
  const assessmentLanguage = normalizeAssessmentLanguage(firstValue(binding.assessment_language,
    ...sources.map(source => source.assessment_language),
    ...sources.map(source => source.defaultLang)));
  const reportLanguage = normalizeAssessmentLanguage(firstValue(
    ...sources.map(source => source.report_language)), 'zh-hans');

  return {
    ...profile,
    useruuid: firstValue(
      urlParams.useruuid,
      userInfo.useruuid,
      activeSession.useruuid,
      matchingSession.useruuid,
    ) || '',
    evaluation_uuid: firstValue(
      urlParams.evaluation_uuid,
      userInfo.evaluation_uuid,
      activeSession.evaluation_uuid,
      matchingSession.evaluation_uuid,
    ) || '',
    defaultLang: uiLanguage(assessmentLanguage),
    assessment_language: assessmentLanguage,
    report_language: reportLanguage,
    question_bank_server_bound: Boolean(binding.question_bank_server_bound),
    ...Object.fromEntries(['question_bank_version', 'question_bank_checksum', 'scoring_version']
      .map(key => [key, binding[key] || undefined])),
    companyCode: firstValue(urlParams.companyCode, activeSession.companyCode, matchingSession.companyCode) || '',
    Job_NID: firstValue(urlParams.Job_NID, activeSession.Job_NID, matchingSession.Job_NID) || '',
    User_NID: firstValue(urlParams.User_NID, activeSession.User_NID, matchingSession.User_NID) || '',
    codeType: firstValue(urlParams.codeType, activeSession.codeType, matchingSession.codeType) || '',
    evaluation_type: firstValue(
      urlParams.evaluation_type,
      activeSession.evaluation_type,
      matchingSession.evaluation_type,
    ) || '',
  };
};

export const createSubmissionId = () => {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `submission-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};
